import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  History,
  RotateCcw,
  Save,
} from 'lucide-react';
import { cn } from '../components/ui/cn.ts';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { BlockPropsPanel } from '../components/cms/VisualBuilder/BlockPropsPanel.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  collectionKey?: string;
}

interface BlockDef {
  id: string;
  name: string;
  slug: string;
  category: string;
  isActive: boolean;
  fieldsJson: FieldDef[];
}

interface SpaceCollection {
  id: string;
  slug: string;
  name: string;
  listingTemplateId: string | null;
  detailTemplateId: string | null;
  fieldsJson: { key: string; label: string; type: string }[];
}

interface TemplateBlock {
  id: string;
  blockDefinitionId: string | null;
  blockSlug: string | null;
  sortOrder: number;
  props: Record<string, unknown>;
  parentBlockId: string | null;
}

interface CollectionTemplate {
  id: string;
  name: string;
  scope: 'listing' | 'detail';
  blocks: TemplateBlock[];
}

interface Version {
  id: string;
  createdAt: string;
  createdBy: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CmsCollectionTemplateEditorPage() {
  const { spaceId, templateId } = useParams<{ spaceId: string; templateId: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const [localBlocks, setLocalBlocks] = useState<TemplateBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);

  // ─── Fetch template ─────────────────────────────────────────────────────────

  const { data: template, isLoading: templateLoading } = useQuery<CollectionTemplate>({
    queryKey: ['collection-template', spaceId, templateId],
    queryFn: () =>
      api
        .get(`/api/v1/cms/spaces/${spaceId}/collection-templates/${templateId}`)
        .then((r) => r.data),
    enabled: !!spaceId && !!templateId,
  });

  // ─── Sync local blocks when template loads ───────────────────────────────────

  useEffect(() => {
    if (template) {
      const sorted = [...template.blocks].sort((a, b) => a.sortOrder - b.sortOrder);
      setLocalBlocks(sorted);
    }
  }, [template]);

  // ─── Fetch block definitions ─────────────────────────────────────────────────

  const { data: blockDefs = [], isLoading: blockDefsLoading } = useQuery<BlockDef[]>({
    queryKey: ['block-definitions'],
    queryFn: () =>
      api
        .get(`/api/v1/cms/block-definitions`)
        .then((r) => r.data),
    enabled: !!spaceId,
  });

  const activeBlockDefs = blockDefs.filter((bd) => bd.isActive);

  // ─── Fetch space collections (to find context collection for this template) ──

  const { data: spaceCollections = [] } = useQuery<SpaceCollection[]>({
    queryKey: ['cms-collections-picker', spaceId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
    staleTime: 60_000,
  });

  const contextCollection = spaceCollections.find(
    (c) => c.listingTemplateId === templateId || c.detailTemplateId === templateId,
  );

  // Group by category
  const defsByCategory = activeBlockDefs.reduce<Record<string, BlockDef[]>>((acc, bd) => {
    const cat = bd.category || 'Altro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(bd);
    return acc;
  }, {});

  // ─── Fetch versions ──────────────────────────────────────────────────────────

  const { data: versions = [], isLoading: versionsLoading } = useQuery<Version[]>({
    queryKey: ['collection-template-versions', spaceId, templateId],
    queryFn: () =>
      api
        .get(`/api/v1/cms/spaces/${spaceId}/collection-templates/${templateId}/versions`)
        .then((r) => r.data),
    enabled: !!spaceId && !!templateId && showVersions,
  });

  // ─── Save mutation ───────────────────────────────────────────────────────────

  const saveMut = useMutation({
    mutationFn: (blocks: TemplateBlock[]) =>
      api
        .put(`/api/v1/cms/spaces/${spaceId}/collection-templates/${templateId}/blocks`, {
          blocks: blocks.map((b, idx) => ({
            id: b.id,
            blockDefinitionId: b.blockDefinitionId,
            sortOrder: idx,
            props: b.props,
            parentBlockId: b.parentBlockId,
          })),
        })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Template salvato');
      void qc.invalidateQueries({ queryKey: ['collection-template', spaceId, templateId] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ─── Restore version mutation ────────────────────────────────────────────────

  const restoreMut = useMutation({
    mutationFn: (versionId: string) =>
      api
        .post(
          `/api/v1/cms/spaces/${spaceId}/collection-templates/${templateId}/versions/${versionId}/restore`,
        )
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Versione ripristinata');
      setShowVersions(false);
      setRestoringVersionId(null);
      void qc.invalidateQueries({ queryKey: ['collection-template', spaceId, templateId] });
    },
    onError: (err) => {
      toast.error(extractError(err));
      setRestoringVersionId(null);
    },
  });

  // ─── Block operations ─────────────────────────────────────────────────────────

  function moveUp(index: number) {
    if (index === 0) return;
    setLocalBlocks((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }

  function moveDown(index: number) {
    setLocalBlocks((prev) => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }

  function removeBlock(blockId: string) {
    setLocalBlocks((prev) => prev.filter((b) => b.id !== blockId));
    setSelectedBlockId((prev) => (prev === blockId ? null : prev));
  }

  function updateBlockProps(blockId: string, newProps: Record<string, unknown>) {
    setLocalBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, props: newProps } : b)),
    );
  }

  function addBlock(blockDef: BlockDef) {
    const newBlock: Omit<TemplateBlock, 'sortOrder'> = {
      id: crypto.randomUUID(),
      blockDefinitionId: blockDef.id,
      blockSlug: blockDef.slug,
      props: {},
      parentBlockId: null,
    };
    setLocalBlocks((prev) => [...prev, { ...newBlock, sortOrder: prev.length }]);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  function getBlockName(block: TemplateBlock): string {
    if (!block.blockDefinitionId) return block.blockSlug ?? 'Block sconosciuto';
    const def = blockDefs.find((bd) => bd.id === block.blockDefinitionId);
    return def?.name ?? block.blockSlug ?? 'Block sconosciuto';
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ─── Loading state ────────────────────────────────────────────────────────────

  if (templateLoading) {
    return (
      <div>
        <PageHeader title="Caricamento..." />
        <div className="space-y-2 mt-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div>
        <PageHeader title="Template non trovato" />
        <p className="text-sm text-zinc-500 mt-4">Il template richiesto non esiste.</p>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to={`/cms/spaces/${spaceId}/collection-templates`}
          className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{template.name}</h1>
          <Badge variant={template.scope === 'listing' ? 'info' : 'purple'}>
            {template.scope}
          </Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowVersions(true)}
          >
            <History className="w-4 h-4 mr-1.5" />
            Versioni
          </Button>
          <Button
            size="sm"
            loading={saveMut.isPending}
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate(localBlocks)}
          >
            <Save className="w-4 h-4 mr-1.5" />
            Salva
          </Button>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex gap-6 flex-1 min-h-0">
        {/* Left panel — blocks list */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
              Blocchi nel template
            </p>
            <button
              onClick={() => setSelectedBlockId(null)}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                selectedBlockId === null
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100',
              )}
              title="Mostra palette per aggiungere blocchi"
            >
              <Plus className="w-3.5 h-3.5" />
              Aggiungi
            </button>
          </div>

          {localBlocks.length === 0 ? (
            <EmptyState
              icon={<Plus size={22} />}
              title="Nessun blocco"
              description="Aggiungi blocchi dalla palette a destra."
            />
          ) : (
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-xl overflow-hidden">
              {localBlocks.map((block, idx) => (
                <div
                  key={block.id}
                  onClick={() => setSelectedBlockId((prev) => (prev === block.id ? null : block.id))}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
                    selectedBlockId === block.id
                      ? 'bg-zinc-100 border-l-2 border-zinc-700'
                      : 'bg-white hover:bg-zinc-50',
                  )}
                >
                  {/* Order controls */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); moveUp(idx); }}
                      disabled={idx === 0}
                      className="p-0.5 rounded text-zinc-300 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveDown(idx); }}
                      disabled={idx === localBlocks.length - 1}
                      className="p-0.5 rounded text-zinc-300 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Block info */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium text-zinc-800 truncate">
                      {getBlockName(block)}
                    </span>
                    {block.blockSlug && (
                      <Badge variant="default">{block.blockSlug}</Badge>
                    )}
                  </div>

                  {/* Remove */}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel — props editor or palette */}
        {(() => {
          const selectedBlock = selectedBlockId
            ? localBlocks.find((b) => b.id === selectedBlockId)
            : null;
          const selectedBlockDef = selectedBlock
            ? (blockDefs.find((bd) => bd.id === selectedBlock.blockDefinitionId) ?? null)
            : null;

          if (selectedBlock) {
            return (
              <BlockPropsPanel
                blockId={selectedBlock.id}
                blockType={selectedBlock.blockSlug ?? ''}
                blockDef={selectedBlockDef}
                props={selectedBlock.props}
                spaceId={spaceId}
                contextCollectionSlug={contextCollection?.slug}
                onUpdate={updateBlockProps}
                onDelete={(id) => removeBlock(id)}
                onClose={() => setSelectedBlockId(null)}
              />
            );
          }

          return (
            <div className="w-72 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-3">
                Aggiungi blocchi
              </p>

              {blockDefsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full rounded-lg" />
                  ))}
                </div>
              ) : activeBlockDefs.length === 0 ? (
                <p className="text-sm text-zinc-400">Nessun blocco disponibile.</p>
              ) : (
                <div className="space-y-5">
                  {Object.entries(defsByCategory).map(([category, defs]) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-zinc-500 mb-2">{category}</p>
                      <div className="space-y-1">
                        {defs.map((bd) => (
                          <div
                            key={bd.id}
                            className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors"
                          >
                            <span className="text-sm text-zinc-700 truncate">{bd.name}</span>
                            <button
                              onClick={() => addBlock(bd)}
                              className="ml-2 shrink-0 p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                              title={`Aggiungi ${bd.name}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Versions modal */}
      <Modal
        open={showVersions}
        onClose={() => setShowVersions(false)}
        title="Versioni salvate"
      >
        {versionsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">Nessuna versione salvata.</p>
        ) : (
          <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-xl overflow-hidden">
            {versions.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div>
                  <p className="text-sm text-zinc-800">{formatDate(v.createdAt)}</p>
                  {v.createdBy && (
                    <p className="text-xs text-zinc-400">{v.createdBy}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={restoringVersionId === v.id && restoreMut.isPending}
                  disabled={restoreMut.isPending}
                  onClick={() => {
                    setRestoringVersionId(v.id);
                    restoreMut.mutate(v.id);
                  }}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Ripristina
                </Button>
              </div>
            ))}
          </div>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowVersions(false)}>
            Chiudi
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
